import { B2bAccount, Database, NotificationV3, PlanScopeV3 } from "#types";
import { signIn, signOut, fetchAuthSession } from '@aws-amplify/auth';
import jwtDecode from "jwt-decode";
import { clientV3 } from "./interceptorV3";

const ADMIN_GROUP: Record<Database, string> = { [Database.ZIM]: 'zim-admins', [Database.SBB]: 'sbb-admins', [Database.GD]: 'gd-admins' };

const login = async (email: string, password: string, db: Database) => {
    try {
        await signOut({ global: true });
        const res = await signIn({ username: email, password });
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString() ?? '';
        const decoded: any = jwtDecode(idToken);
        const groups = decoded['cognito:groups'] ?? [];
        if (!groups.includes(ADMIN_GROUP[db])) {
            await signOut({ global: true });
            throw new Error('Not an admin');
        }
        return res;
    } catch (error) {
        await signOut({ global: true });
        console.error('Login failed: ', error);
        throw error;
    }
};

const logOut = async () => {
    try {
        await signOut({ global: true });
    } catch (error) {
        console.error('Error logging out: ', error);
        throw error;
    }
};

const getToken = async () => {
    try {
        const session = await fetchAuthSession();
        return session?.tokens?.accessToken?.toString() || '';
    } catch (error) {
        console.error('Error retrieving session token: ', error);
        throw error;
    }
};

const getProfile = () => {
    const route = `/staff/profile`
    return clientV3.get(route)
}

const getUsers = (pageNumber?: number, pageSize?: number) => {
    const route = `/staff/users/all?pageNumber=${pageNumber}&pageSize=${pageSize}`;
    return clientV3.get(route);
};

const searchUsersByEmail = (email: string, includeEsims: boolean) => {
    const route = `/staff/user?email=${email}&includeEsims=${includeEsims}`;
    return clientV3.get(route);
};

const getPlans = (iso3: string, scope: PlanScopeV3) => {
    const queryParams = new URLSearchParams();
    if (scope) queryParams.set('scope', scope);
    if (scope === PlanScopeV3.LOCAL) queryParams.set('iso3', iso3);
    const route = `/staff/plans?${queryParams.toString()}`;
    return clientV3.get(route);
};

const assignEsim = (userUUID: string, productNanoID?: string, stripeTransactionId?: string) => {
    const route = '/staff/assignEsim'
    const body = { userUUID, productNanoID, stripeTransactionId }
    return clientV3.post(route, body);
}

const fetchDestinations = () => {
    const route = '/staff/destinations'
    return clientV3.get(route);
}

const getCustomStats = () => {
    const route = '/staff/customStats'
    return clientV3.get(route);
}

const fetchActivity = (subscriptionNanoID: string) => {
    const route = `/staff/subscriptionActivity/${subscriptionNanoID}`;
    return clientV3.get(route);
}

const sendNotification = (requestBody: NotificationV3) => {
    const route = '/staff/notification';
    return clientV3.post(route, requestBody);
}

const getB2bAccounts = () => {
    const route = `/staff/b2b/all`;
    return clientV3.get(route);
};

const createB2bAccount = (requestBody: Partial<B2bAccount>) => {
    const route = '/staff/b2b/create';
    return clientV3.post(route, requestBody);
}

const updateAccount = (requestBody: Partial<B2bAccount>) => {
    const route = `/staff/b2b/update`;
    return clientV3.put(route, requestBody);
}

export const dashboardApiV3 = {
    login,
    logOut,
    getToken,
    getUsers,
    getProfile,
    getPlans,
    assignEsim,
    fetchDestinations,
    searchUsersByEmail,
    getCustomStats,
    fetchActivity,
    sendNotification,
    getB2bAccounts,
    createB2bAccount,
    updateAccount
}